/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
import { logger, sanitizeError } from "../utils/logger.js";
/**
 * @fileoverview Users page: list, search, sort, and paginate users with Directory-style load more.
 * @module pages/Users
 */

import styles from "../styles/NotificationCenter.module.css";

import { ReactComponent as TitleIcon } from "../assets/icons/Users/TitleIcon.svg";
import { ReactComponent as AddIcon } from "../assets/icons/UserViews/AddIcon.svg";
import { AppContext } from "../context/AppContext.js";
import { getUser, getUserRoleData } from "../logic/user/index.js";
import {
  initializeNotificationsPage,
  fetchMoreNotificationsPage,
  sortNotifications,
  filterNotifications,
  paginateNotifications,
  downloadNotificationFile,
} from "../logic/customNotification/index.js";
import LanguageSelector from "../components/LanguageSelector/index.jsx";
import NotificationBell from "../components/NotificationBell/index.jsx";
import NotificationsTable from "../components/NotificationsTable/index.jsx";
import ResultsDropdown from "../components/ResultsDropdown/index.jsx";
import SearchBar from "../components/SearchBar/index.jsx";
import PageSelector from "../components/PageSelector/index.jsx";

import { Backdrop, CircularProgress } from "@mui/material";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";

import { use, useState, useEffect } from "react";
import { useNavigate } from "react-router";

import { useTranslation } from "react-i18next";
import { canAccessCustomNotifications } from "../utils/accessControl";
import useRequireAccess from "../hooks/useRequireAccess";

/**
 * Users page: list, search, sort, and paginate users with Directory-style "load more".
 * Shows user details/edit/creation in modals (desktop) or full views (tablet/mobile).
 * Data is loaded via a single aggregate endpoint (first page + cursor-based load more).
 *
 * @returns {JSX.Element} The Users page component.
 */
const Users = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { allowed } = useRequireAccess(canAccessCustomNotifications);
  const { changePermissions } = use(AppContext);
  const [usersData, setUsersData] = useState([]);
  const [sortedNotificationsList, setSortedNotificationsList] = useState([]);
  const [sortConfig, setSortConfig] = useState({
    key: "asunto",
    direction: "ascending",
  });
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [resultsLimit, setResultsLimit] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [user, setUser] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [lastKey, setLastKey] = useState(null);
  const [limitReached, setLimitReached] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem("token");

    if (!!token && token.trim() !== "undefined") {
      getData();
    } else {
      logger.info("Error trying to get the token to retrieve the users data.");
    }
  }, []);

  /**
   * Loads the first page of users and companies from page-data, then fetches current user
   * and permissions from the same endpoints as Directory/BusinessEvents (GET /user/:userId,
   * GET /permission/:userId) so behavior stays consistent after refetches (e.g. after saving a user).
   * Resets lastKey and limitReached. Called on mount (when token exists) and after updating a user.
   * @returns {Promise<void>}
   */
  const getData = async () => {
    try {
      setIsLoading(true);
      const userId = localStorage.getItem("userId");
      const data = await initializeNotificationsPage(navigate);
      setNotifications(data.notifications ?? []);
      setLastKey(data.lastEvaluatedKey ?? null);
      setLimitReached(false);

      const [userData, permisos] = await Promise.all([
        getUser(userId, navigate),
        getUserRoleData(userId, navigate),
      ]);
      if (userData) {
        setUser(userData);
      }
      if (permisos != null) {
        changePermissions(permisos);
      }
    } catch (error) {
      logger.error("Error", { err: sanitizeError(error) });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const sortedList = sortNotifications(notifications, sortConfig);
    setSortedNotificationsList(sortedList);
  }, [notifications, sortConfig.direction]);

  /**e
   * Toggles sort direction for the notifications table (ascending ↔ descending by subject).
   */
  const handleSort = () => {
    const newDirection =
      sortConfig.direction === "ascending" ? "descending" : "ascending";
    setSortConfig({ key: "asunto", direction: newDirection });
  };

  /**
   * Renders the sort direction arrow icon (rotated when ascending).
   * @returns {JSX.Element} Arrow icon for the subject column header.
   */
  const renderArrow = () => {
    return (
      <ArrowDownwardIcon
        className={`${styles.arrowIcon} ${
          sortConfig.direction === "ascending" ? styles.rotated : ""
        }`}
      />
    );
  };

  /**
   * Updates the search query from the search input (filters notifications by subject).
   * @param {React.ChangeEvent<HTMLInputElement>} event - Change event from the search input.
   */
  const handleSearch = (event) => {
    setSearchQuery(event.target.value);
  };

  /**
   * Updates how many notifications are shown per page and resets to page 1.
   * @param {{ value: number }} selectedOption - Option from ResultsDropdown (e.g. { value: 10 }).
   */
  const handleResultsLimitChange = (selectedOption) => {
    setResultsLimit(selectedOption.value);
    setCurrentPage(1);
  };

  /**
   * Appends a newly created notification to the list and closes the creation modal/view.
   * @param {Object} newNotification - The notification object returned from the creation flow.
   */
  const handleAddNotification = (newNotification) => {
    setNotifications((prevNotifications) => {
      if (!Array.isArray(prevNotifications)) {
        return [newNotification];
      }
      return [...prevNotifications, newNotification];
    });
    setOpen(false);
  };

  /**
   * Handles prev/next pagination. On "next" when on the last page, fetches more notifications via lastKey
   * (Directory-style load more); disables next when no more data (limitReached).
   * @param {"prev"|"next"} direction - Which page to go to.
   * @returns {Promise<void>}
   */
  const handlePageChange = async (direction) => {
    const totalPages = Math.ceil(
      filteredNotificationsList.length / resultsLimit,
    );
    if (direction === "next") {
      if (currentPage < totalPages) {
        setCurrentPage((prevPage) => prevPage + 1);
      } else if (lastKey) {
        try {
          setIsLoading(true);
          const next = await fetchMoreNotificationsPage(navigate, lastKey);
          setNotifications((prev) => [...prev, ...next.notifications]);
          setLastKey(next.lastEvaluatedKey ?? null);
          setCurrentPage((prevPage) => prevPage + 1);
        } catch (error) {
          logger.error("Error fetching more notifications:", {
            err: sanitizeError(error),
          });
        } finally {
          setIsLoading(false);
        }
      } else {
        setLimitReached(true);
      }
    } else if (direction === "prev" && currentPage > 1) {
      setCurrentPage((prevPage) => prevPage - 1);
      setLimitReached(false);
    } else {
      // Unsupported direction or already at bounds. No action required.
    }
  };

  /**
   * @returns {boolean} True if viewport is tablet width (768–1023px).
   */
  const isTablet = () => {
    return window.innerWidth >= 768 && window.innerWidth <= 1023;
  };

  /**
   * @returns {boolean} True if viewport is medium mobile width (481–767px).
   */
  const isMediumMobile = () => {
    return window.innerWidth >= 481 && window.innerWidth <= 767;
  };

  /**
   * @returns {boolean} True if viewport is small mobile width (≤480px).
   */
  const isSmallMobile = () => {
    return window.innerWidth <= 480;
  };

  const handleDownload = async (notification) => {
    logger.info("Downloading notification:", { data: notification });
    try {
      const s3Link = await downloadNotificationFile(notification._id, navigate);
      if (s3Link) {
        const a = document.createElement("a");
        a.href = s3Link;
        a.download = "NotificationRecipients.xlsx";
        a.click();
      } else {
        logger.error("Error downloading notification file");
      }
    } catch (error) {
      logger.error("Error", { err: sanitizeError(error) });
    }
  };

  // Filter users based on search query and user's collaborator status
  const filteredNotificationsList = filterNotifications(
    sortedNotificationsList,
    searchQuery,
  );

  const paginatedNotificationsList = paginateNotifications(
    filteredNotificationsList,
    currentPage,
    resultsLimit,
  );

  if (!allowed) {
    return null;
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.languages}>
          <NotificationBell navigate={navigate} />
          <LanguageSelector />
        </div>

        {/* Page title and search bar */}
        <div className={styles.headerInfoContainer}>
          <div className={styles.headerInfo}>
            <TitleIcon />
            <h1 className={styles.title}>{t("pages.notifications.header")}</h1>
          </div>
          <div className={styles.searchSection}>
            <div className={styles.searchBarWrapper}>
              <SearchBar
                placeholder={t("pages.notifications.searchText")}
                value={searchQuery}
                onChange={handleSearch}
                onSearch={() => {}}
                width="100%"
              />
            </div>
            {user &&
              user?.tipoDeUsuario !== "3" &&
              user?.tipoDeUsuario !== "4" &&
              user?.tipoDeUsuario !== "12" && (
                <div
                  className={styles.newUserButton}
                  onClick={() =>
                    navigate("/notificationForm", {
                      state: {
                        isEditMode: false,
                      },
                    })
                  }
                >
                  <AddIcon />
                  {isTablet()
                    ? t("pages.notifications.tabletNewNotification")
                    : isSmallMobile() || isMediumMobile()
                      ? ""
                      : t("pages.notifications.newNotification")}
                </div>
              )}
          </div>
        </div>
      </div>

      <div className={styles.mainContent}>
        <NotificationsTable
          notifications={paginatedNotificationsList}
          sortConfig={sortConfig}
          onSort={handleSort}
          renderArrow={renderArrow}
          currentUser={user}
          onDownload={handleDownload}
        />

        <div className={styles.footerContainer}>
          <div className={styles.footer}>
            {/* Number of results dropdown */}
            <ResultsDropdown onResultsLimitChange={handleResultsLimitChange} />

            {/* Page selector (Directory-style: next disabled only when limitReached) */}
            <PageSelector
              currentPage={currentPage}
              totalPages={Math.ceil(
                filteredNotificationsList.length / resultsLimit,
              )}
              onPageChange={handlePageChange}
              disabled={{ next: limitReached }}
            />
          </div>
        </div>
      </div>

      <Backdrop
        sx={{
          color: "#fff",
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backgroundColor: "rgba(0, 0, 0, 0.5)",
          backdropFilter: "blur(4px)",
        }}
        open={isLoading}
      >
        <CircularProgress
          size={60}
          thickness={4}
          sx={{
            color: "#4eabe7",
          }}
        />
      </Backdrop>
    </div>
  );
};

export default Users;
